// The standard brain vocabulary: the conditions, actions and scorers a combat behaviour tree may name, moved here from
// spike 002's game glue (spikes/green-valley/src/enemies/brains.ts) so enemy archetypes can travel between games. Each
// reads one actor's view of the fight (`BrainContext`) and acts only through ports the game hands it: a mover (where to
// go: a crowd, a steering layer), the attack-token pool, and a combat port that asks for a move by intent (never the
// combat system's internals). Nothing here draws random numbers (the context's `random` is the game's seeded stream) or
// reads the clock (`dt` comes in). A game uses it as it is (`standardBrains()`), or adds its own names on top.
import type { BehaviourRegistry, Status } from "./bt";
import type { PerceptionState, Vec3 } from "./perception";
import type { AttackTokens } from "./tokens";

/** The actor a brain drives: the fields the vocabulary reads and writes (a game's saved enemy state has these and more). */
export interface BrainActor {
  /** Its id in the mover, the token pool and combat (one id for all three). */
  id: number;
  /** 1, or a boss's later phase (2…). */
  phase: number;
  /** Where it wanders around. */
  home: Vec3;
  pos: Vec3;
  /** Yaw (radians, 0 faces +z). */
  facing: number;
  percept: PerceptionState;
  /** The point it is wandering to, and how long it still waits there. */
  wanderAt: Vec3 | null;
  wanderWait: number;
  /** A boss's summon used in its current phase. */
  summoned: boolean;
}

/** An actor kind's numbers as the vocabulary reads them (an archetype's speeds, token priority and phases). */
export interface BrainProfile {
  speed: { walk: number; run: number; turn: number };
  /** Higher is granted an attack token first. */
  tokenPriority: number;
  /** A boss's later phases (index 0 is phase 2): the summon each one may make. */
  phases: readonly { summon: { kind: string; count: number } | null }[];
}

/** Where to move: a crowd or steering layer, by actor id. */
export interface BrainMover {
  /** Walk toward `at`, stopping within `stop` m, at up to `speed` m/s. */
  moveTo(id: number, at: Vec3, stop?: number, speed?: number): void;
  /** Move at a ground velocity (m/s) this step. */
  moveVelocity(id: number, v: Vec3): void;
  stop(id: number): void;
  /** The actor's agent: whether it has arrived at its last `moveTo` goal. */
  agent(id: number): { readonly arrived: boolean } | undefined | null;
}

/** The combat port: ask for a move by intent, read whether the actor is stunned or mid-move, set its target. */
export interface BrainCombat {
  /** Ask for the move bound to `intent.kind` ("attack", "skill", the game's intents) toward `dir`; false when refused. */
  request(entity: number, intent: { kind: string; dir?: Vec3 }): boolean;
  /** Its fighter now: stun seconds left and the move it is in (null when none), or null when it has no fighter. */
  state(entity: number): { stunned: number; move: unknown } | null;
  setTarget(entity: number, target: number | null): void;
}

/** What a brain sees and can do, for one actor on one tick. */
export interface BrainContext<A extends BrainActor = BrainActor, P extends BrainProfile = BrainProfile> {
  e: A;
  arch: P;
  /** Speed scale now (a phase's, a slow status's): scales walking, running and turning. */
  speedScale: number;
  dt: number;
  /** The target (the hero). */
  hero: { id: number; pos: Vec3; alive: boolean };
  crowd: BrainMover;
  tokens: AttackTokens;
  combat: BrainCombat;
  /** The moves it has now by intent (its phase's): an intent with no move can't be struck or scored. */
  moveset: Readonly<Partial<Record<string, string>>>;
  /** A point on the walkable ground near `p`, or null. */
  snap(p: Vec3): Vec3 | null;
  /** A seeded number in [0, 1) (the game's saved stream). */
  random(): number;
  /** Spawn `count` of `kind` around the actor (a boss's summon). */
  summon(kind: string, count: number): void;
}

/** Planted defects (switched by a game's or a test's checks; each beside what it breaks). */
export const BRAIN_FAULTS = {
  /** The brains ignore the token pool: `askToken` succeeds and `strike` swings without holding a token, so a crowd attacks
   *  at once: the "never more attackers than the cap" check must go red. */
  ignoreTokens: false,
};

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a[0] - b[0], a[2] - b[2]);
const yawTo = (a: Vec3, b: Vec3): number => Math.atan2(b[0] - a[0], b[2] - a[2]);
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const num = (v: unknown, d: number): number => (typeof v === "number" ? v : d);

/** Turn toward `yaw` at the profile's turn rate (scaled); returns the angle left. */
export function turnToward(b: BrainContext, yaw: number): number {
  const d = wrap(yaw - b.e.facing);
  const step = b.arch.speed.turn * b.speedScale * b.dt;
  b.e.facing = wrap(b.e.facing + Math.max(-step, Math.min(step, d)));
  return Math.abs(wrap(yaw - b.e.facing));
}

/** The standard vocabulary. Arguments come from the tree's JSON (defaults in brackets). */
const STANDARD: BehaviourRegistry<BrainContext> = {
  conditions: {
    /** Combat has it stunned. */
    stunned: (b) => (b.combat.state(b.e.id)?.stunned ?? 0) > 0,
    /** It is in a move (winding up, striking, recovering). */
    busy: (b) => b.combat.state(b.e.id)?.move != null,
    alert: (b) => b.e.percept.level === "alert",
    suspicious: (b) => b.e.percept.level === "suspicious",
    targetAlive: (b) => b.hero.alive,
    /** Within `max` [2] m of the target. */
    near: (b, a) => dist(b.e.pos, b.hero.pos) <= num(a.max, 2),
    holdsToken: (b) => b.tokens.holds(b.e.id),
    /** In phase `n` [1]. */
    phase: (b, a) => b.e.phase === num(a.n, 1),
    /** Its phase has a summon it hasn't used. */
    canSummon: (b) => !b.e.summoned && (b.arch.phases[b.e.phase - 2]?.summon ?? null) !== null,
  },
  actions: {
    /** Stand still (running: a fallback leaf). */
    rest: (b) => {
      b.crowd.stop(b.e.id);
      return "running";
    },
    /** Stroll between seeded points within `radius` [3] m of home, pausing `pause` [2] s at each (running). */
    wander: (b, a) => {
      const e = b.e;
      if (e.wanderWait > 0) {
        e.wanderWait = Math.max(0, e.wanderWait - b.dt);
        b.crowd.stop(e.id);
        return "running";
      }
      if (!e.wanderAt) {
        const ang = b.random() * Math.PI * 2;
        const r = num(a.radius, 3) * (0.3 + 0.7 * b.random());
        e.wanderAt = b.snap([e.home[0] + Math.sin(ang) * r, e.home[1], e.home[2] + Math.cos(ang) * r]) ?? [...e.home];
        b.crowd.moveTo(e.id, e.wanderAt, 0.3, b.arch.speed.walk * b.speedScale);
      }
      if (b.crowd.agent(e.id)?.arrived) {
        e.wanderAt = null;
        e.wanderWait = num(a.pause, 2);
      }
      return "running";
    },
    /** Walk to where the target was last known, then look about for `look` [2] s. */
    investigate: (b, a, m) => {
      const at = b.e.percept.lastKnown;
      if (!at) return "success";
      b.crowd.moveTo(b.e.id, at, 0.8, b.arch.speed.walk * b.speedScale);
      if (!b.crowd.agent(b.e.id)?.arrived) return "running";
      m.t = num(m.t, 0) + b.dt;
      b.e.facing = wrap(b.e.facing + b.dt * 1.2);
      return (m.t as number) >= num(a.look, 2) ? "success" : "running";
    },
    /** Close on the target until within `stop` [2] m. */
    approach: (b, a) => {
      const stop = num(a.stop, 2);
      if (dist(b.e.pos, b.hero.pos) <= stop) {
        b.crowd.stop(b.e.id);
        return "success";
      }
      b.crowd.moveTo(b.e.id, b.hero.pos, stop * 0.9, b.arch.speed.run * b.speedScale);
      return "running";
    },
    /** Orbit the target at `radius` [4] m and up to `speed` [2] m/s (the way round from the id's parity): what the
     *  others do while one holds the token. */
    circle: (b, a) => {
      const r = num(a.radius, 4);
      const speed = Math.min(num(a.speed, 2), b.arch.speed.run) * b.speedScale;
      const dx = b.e.pos[0] - b.hero.pos[0];
      const dz = b.e.pos[2] - b.hero.pos[2];
      const d = Math.hypot(dx, dz) || 1;
      const nx = dx / d;
      const nz = dz / d;
      const s = b.e.id % 2 === 0 ? 1 : -1;
      const radial = Math.max(-1, Math.min(1, (r - d) / 1.5));
      b.crowd.moveVelocity(b.e.id, [(-nz * s * 0.8 + nx * radial) * speed, 0, (nx * s * 0.8 + nz * radial) * speed]);
      return "running";
    },
    /** Hold between `min` [6] and `max` [10] m of the target: back off, close in, or succeed in the band. */
    keepRange: (b, a) => {
      const d = dist(b.e.pos, b.hero.pos);
      const min = num(a.min, 6);
      const max = num(a.max, 10);
      if (d < min) {
        const away = yawTo(b.hero.pos, b.e.pos);
        b.crowd.moveVelocity(b.e.id, [Math.sin(away) * b.arch.speed.run * b.speedScale, 0, Math.cos(away) * b.arch.speed.run * b.speedScale]);
        return "running";
      }
      if (d > max) {
        b.crowd.moveTo(b.e.id, b.hero.pos, max - 0.5, b.arch.speed.run * b.speedScale);
        return "running";
      }
      b.crowd.stop(b.e.id);
      return "success";
    },
    /** Back away until `to` [8] m off (fails after 2 s cornered, so the tree tries something else). */
    retreat: (b, a, m) => {
      if (dist(b.e.pos, b.hero.pos) >= num(a.to, 8)) return "success";
      m.t = num(m.t, 0) + b.dt;
      if ((m.t as number) > 2) return "failure";
      const away = yawTo(b.hero.pos, b.e.pos) + (b.e.id % 2 === 0 ? 0.5 : -0.5);
      const v = b.arch.speed.run * b.speedScale;
      b.crowd.moveVelocity(b.e.id, [Math.sin(away) * v, 0, Math.cos(away) * v]);
      return "running";
    },
    /** Hold an attack token (ask for one at `priority` [the profile's] and the distance): success while holding. */
    askToken: (b, a) => {
      if (b.tokens.holds(b.e.id) || BRAIN_FAULTS.ignoreTokens) return "success";
      b.tokens.request(b.e.id, { priority: num(a.priority, b.arch.tokenPriority), distance: dist(b.e.pos, b.hero.pos) });
      return "failure";
    },
    /** Turn to face the target, standing; success within `tolerance` [0.3] radians. */
    face: (b, a) => {
      b.crowd.stop(b.e.id);
      return turnToward(b, yawTo(b.e.pos, b.hero.pos)) <= num(a.tolerance, 0.3) ? "success" : "running";
    },
    /** Ask combat for the move bound to `intent` ["attack"], holding a token; the token's timer restarts so it can't
     *  run out under the wind-up. */
    strike: (b, a): Status => {
      const kind = (a.intent as string | undefined) ?? "attack";
      if (!b.moveset[kind] || (!b.tokens.holds(b.e.id) && !BRAIN_FAULTS.ignoreTokens)) return "failure";
      b.combat.setTarget(b.e.id, b.hero.id);
      const ok = b.combat.request(b.e.id, { kind, dir: [Math.sin(b.e.facing), 0, Math.cos(b.e.facing)] });
      if (!ok) return "failure";
      b.tokens.renew(b.e.id);
      b.crowd.stop(b.e.id);
      return "success";
    },
    /** A boss's summon for its phase (once per phase). */
    summon: (b) => {
      const s = b.arch.phases[b.e.phase - 2]?.summon;
      if (s) b.summon(s.kind, s.count);
      b.e.summoned = true;
      return "success";
    },
    /** Give the token back. */
    releaseToken: (b) => {
      b.tokens.release(b.e.id, "cancel");
      return "success";
    },
  },
  scorers: {
    /** How well the move for `intent` fits the distance: `weight` [1] at `best` [2] m, falling to 0 at `spread` [2] m
     *  off; 0 without the move. */
    fit: (b, a) => {
      const kind = a.intent as string;
      if (!b.moveset[kind]) return 0;
      const d = dist(b.e.pos, b.hero.pos);
      return num(a.weight, 1) * Math.max(0, 1 - Math.abs(d - num(a.best, 2)) / num(a.spread, 2));
    },
  },
};

/** The standard vocabulary, typed for a game's own context (which has at least `BrainContext`'s fields). */
export function standardBrains<C extends BrainContext = BrainContext>(): BehaviourRegistry<C> {
  return STANDARD as unknown as BehaviourRegistry<C>;
}

/** The names the standard vocabulary registers (validate a tree against them with `validateTree`). */
export const STANDARD_BRAIN_NAMES = {
  conditions: Object.keys(STANDARD.conditions),
  actions: Object.keys(STANDARD.actions),
  scorers: Object.keys(STANDARD.scorers ?? {}),
};
